import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { AuthService } from './auth.service';
import { ToastrService } from 'ngx-toastr';
import i18next from 'i18next';
import { NativeIntegrationService } from './native-integration.service';
import { TermsService } from 'src/core/services/term.service';
import { DataService } from './data.service';
import { hasBiometrics } from 'src/util';
import { AppUpdateService } from 'src/modules/shared/services/app-update.service';

type AuthState =
  | 'login'
  | 'biologin'
  | 'terms'
  | 'package-download'
  | 'setpin'
  | 'home';

@Injectable()
export class AuthStateService implements OnDestroy {
  private readonly currentState = new BehaviorSubject<AuthState>('login');

  subscriptions: Subscription[] = [];
  isLoadingSession = false;

  constructor(
    private router: Router,
    private authService: AuthService,
    private toastr: ToastrService,
    private nativeIntegrationService: NativeIntegrationService,
    private termsService: TermsService,
    private dataService: DataService,
    private appUpdateService: AppUpdateService
  ) {
    const stateSub = this.currentState.subscribe((state) =>
      this.transitionState(state)
    );
    this.subscriptions.push(stateSub);

    const loginSub = this.currentState.subscribe((state) => {
      if (state === 'login') {
        this.authService.checkIfLoggedIn().then(async (x) => {
          console.log('[AUTH STATE]: Check if logged in -> ' + x);
          if (x) {
            if (await this.nativeIntegrationService.readyForOfflineMode()) {
              this.dataService.urlParamFile = '';
              this.dataService.urlParamContext = '';
              this.dataService.apiUrl = `http://localhost:${this.nativeIntegrationService.native.port}/xwc/pack/latest/data`;
              //this.authService.loadSession();
            }
          } else {
            console.log(
              '[AUTH STATE]: Init -> user logged out. Clear local storage.'
            );
            this.dataService.logout();
          }
        });
      }
    });
    this.subscriptions.push(loginSub);

    const invariantSub = combineLatest([
      this.currentState,
      this.authService.isAuthenticated$,
      this.authService.hasPin$,
      this.authService.username$,
      this.termsService.currentUserDidAcceptTerms$,
      this.nativeIntegrationService.isNative$,
      this.nativeIntegrationService.isReady$,
      this.dataService.loading$,
      this.dataService.dataLoaded$,
      this.authService.isAuthenticatedOffline$,
      this.appUpdateService.didCheckForUpdate$,
    ]).subscribe(
      async ([
        currentState,
        isAuthenticated,
        hasPin,
        username,
        termsAccepted,
        isNative,
        isReady,
        loading,
        dataLoaded,
        isAuthenticatedOffline,
        didCheckForUpdate,
      ]) => {
        if (!isReady || loading || !didCheckForUpdate) {
          console.log(
            '[AUTH STATE]: Not ready or loading or did not check for update',
            !isReady,
            loading,
            !didCheckForUpdate
          );
          return;
        }

        if (!dataLoaded) {
          console.log(
            `[AUTH STATE]: Data not loaded isNative: ${isNative} isAuthenticated: ${isAuthenticated} isAuthenticatedOffline: ${isAuthenticatedOffline}`
          );
          if (isNative && isAuthenticated && isAuthenticatedOffline) {
            if (username == null) {
              this.dataService.logout();
              return;
            }
            this.toastr.success(
              i18next.t('login.login-success-welcome') +
                ' <strong>' +
                username +
                '</strong> ' +
                i18next.t('login.login-success-txt'),
              i18next.t('login.login-success-title')
            );
            this.dataService.getContext();
          } else if (isNative && isAuthenticated && !isAuthenticatedOffline) {
            const supportsBiometrics = await hasBiometrics(
              this.nativeIntegrationService
            );
            if (!supportsBiometrics) {
              // WTF
              console.log(
                '[AUTH STATE]: Biometric Auth not supported when loading session'
              );
              return;
            }
            if (this.isLoadingSession) {
              console.log('[AUTH STATE]: Loading session already loading');
              return;
            }
            this.isLoadingSession = true;
            this.authService.loadSession().finally(() => {
              this.isLoadingSession = false;
            });
          } else if (isAuthenticated) {
            if (username == null) {
              this.dataService.logout();
              return;
            }
            this.toastr.success(
              i18next.t('login.login-success-welcome') +
                ' <strong>' +
                username +
                '</strong> ' +
                i18next.t('login.login-success-txt'),
              i18next.t('login.login-success-title')
            );
            this.dataService.getContext();
          }
          return;
        } else {
          console.log(
            `[AUTH STATE]: Data loaded isNative: ${isNative} isAuthenticated: ${isAuthenticated} isAuthenticatedOffline: ${isAuthenticatedOffline}`
          );
        }

        if (!isNative) {
          if (!isAuthenticated) {
            if (currentState === 'login') return;
            console.log('[AUTH STATE]: Not native -> login');
            this.pushState('login');
          } else if (!termsAccepted) {
            // isAuthenticated = true
            if (currentState === 'terms') return;
            console.log('[AUTH STATE]: Not native -> terms');
            this.pushState('terms');
          } else if (termsAccepted) {
            // isAuthenticated = true
            // termsAccepted = true
            if (currentState === 'package-download') return;
            console.log('[AUTH STATE]: Not native -> package-download');
            this.pushState('package-download');
          }
        } else {
          if (!isAuthenticated) {
            if (currentState === 'login') return;
            console.log('[AUTH STATE]: Native -> login');
            this.pushState('login');
          } else if (!hasPin) {
            // isAuthenticated = true
            if (currentState === 'setpin') return;
            console.log('[AUTH STATE]: Native -> setpin');
            this.pushState('setpin');
          } else if (!termsAccepted) {
            // isAuthenticated = true
            // hasPin = true
            if (currentState === 'terms') return;
            console.log('[AUTH STATE]: Native -> terms');
            this.pushState('terms');
          } else if (termsAccepted) {
            // isAuthenticated = true
            // hasPin = true
            // termsAccepted = true
            if (currentState === 'package-download') return;
            console.log('[AUTH STATE]: Native -> package-download');
            this.pushState('package-download');
          }
        }
      }
    );
    this.subscriptions.push(invariantSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  pushState(state: AuthState) {
    this.currentState.next(state);
  }

  private transitionState(state: AuthState) {
    switch (state) {
      case 'login':
        this.router.navigate(['/']);
        break;
      case 'biologin':
        this.router.navigate(['/biologin']);
        break;
      case 'terms':
        this.router.navigate(['/auth/terms']);
        break;
      case 'package-download':
        this.router.navigate(['/pack-download']);
        break;
      case 'setpin':
        this.router.navigate(['/auth/set-pin']);
        break;
      case 'home':
        this.router.navigate(['/home']);
        break;
    }
  }

  getCurrentState() {
    return this.currentState.asObservable();
  }
}
